import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import isNil from 'lodash/isNil';

const UTC_TIMEZONE = 'UTC';

/**
 * Serialization format constants — must match backend
 * DateVariable::FORMAT ('Y-m-d') and DatetimeVariable::FORMAT ('Y-m-d\TH:i:s.v\Z').
 */
export const DATE_SERIALIZATION_FORMAT = 'yyyy-MM-dd';
export const DATETIME_SERIALIZATION_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
export const DATETIME_SERIALIZATION_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

/** Serialize a Date to YYYY-MM-DD (local time). */
export function serializeDate(date: Date): string {
  return format(date, DATE_SERIALIZATION_FORMAT);
}

/** Serialize a Date to full ISO-8601 datetime in UTC. */
export function serializeDatetime(date: Date): string {
  return formatInTimeZone(date, UTC_TIMEZONE, DATETIME_SERIALIZATION_FORMAT);
}

export function formatDate(date: string): string;
export function formatDate(date: string | null | undefined): string | null;
export function formatDate(date: string | null | undefined): string | null {
  if (!date) {
    return null;
  }

  return format(parseISO(date), 'MMMM d, yyyy');
}

export function formatDateTime(date: string): string;
export function formatDateTime(date: string | null | undefined): string | null;
export function formatDateTime(date: string | null | undefined): string | null {
  if (!date) {
    return null;
  }

  return format(parseISO(date), 'MMM d, yyyy, h:mm a');
}

/** Like formatDateTime, with seconds — e.g. "Sep 22, 2026, 8:36:12 PM". */
export function formatDateTimeWithSeconds(date: string): string;
export function formatDateTimeWithSeconds(date: string | null | undefined): string | null;
export function formatDateTimeWithSeconds(date: string | null | undefined): string | null {
  if (!date) {
    return null;
  }

  return format(parseISO(date), 'MMM d, yyyy, h:mm:ss a');
}

/**
 * Compact enough to sit beside a label in a dropdown row without wrapping —
 * e.g. "10/24/24 7:14 PM".
 */
export function formatCompactDateTime(date: string): string;
export function formatCompactDateTime(date: string | null | undefined): string | null;
export function formatCompactDateTime(date: string | null | undefined): string | null {
  if (!date) {
    return null;
  }

  return format(parseISO(date), 'M/d/yy h:mm a');
}

// Full-precision datetime including seconds, milliseconds and the am/pm marker, for debug views
// where the exact moment (and ordering within the same minute) matters.
export function formatPreciseDateTime(date: string): string;
export function formatPreciseDateTime(date: string | null | undefined): string | null;
export function formatPreciseDateTime(date: string | null | undefined): string | null {
  if (!date) {
    return null;
  }

  return format(parseISO(date), 'MMM d, yyyy, h:mm:ss.SSS a');
}

const timeZoneNameFormatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });

// The browser's short timezone name for a given instant (e.g. "EDT", "PST", "GMT+2"). Derived per
// value so it stays correct across DST boundaries rather than assuming the current offset.
function localTimeZoneAbbreviation(date: Date): string {
  return (
    timeZoneNameFormatter.formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ??
    ''
  );
}

// Formats an absolute timestamp in the browser's local timezone (never UTC), appending the timezone
// abbreviation so it's unambiguous which zone the time is shown in.
export function formatLocalTime(
  value: Date | number | string | null | undefined,
  pattern: string
): string {
  if (isNil(value)) {
    return '--';
  }

  const date = new Date(value);
  const abbreviation = localTimeZoneAbbreviation(date);
  const time = format(date, pattern);

  return abbreviation === '' ? time : `${time} ${abbreviation}`;
}
